// src/components/Admin/PostManager/PostsTable.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, TextField, TableContainer, Paper, Table, TableHead, TableRow,
  TableCell, TableBody, Pagination, Stack, Button, CircularProgress,
  Alert, MenuItem, Select, Typography, IconButton, Tooltip,
  useMediaQuery, Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useUser } from '../../../contexts/UserContext';
import api from '../../../api/axios';
import ReasonModal from './ReasonModal';
import * as XLSX from 'xlsx';

const ROWS_PER_PAGE = 15;

export default function PostsTable() {
  const { userInfo } = useUser();
  const theme = useTheme();

  // ✅ 모바일은 "한 화면" 목적이니까 sm 기준으로 카드로 바꿈
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // ≤600
  const isTabletOrBelow = useMediaQuery(theme.breakpoints.down('md')); // 아이콘 UI 용(PC 버튼 vs 아이콘)

  const [allPosts, setAllPosts] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  /* ───────── 데이터 로드 ───────── */
  const fetchAllPosts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/posts/all/');
      setAllPosts(res.data.results || []);
    } catch {
      setError('게시글 목록을 불러오는 데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAllPosts(); }, [fetchAllPosts]);

  /* ───────── 필터링 ───────── */
  const filteredPosts = useMemo(() => (
    allPosts
      .filter((p) => {
        if (statusFilter === 'visible') return !p.is_deleted && !p.is_hidden;
        if (statusFilter === 'hidden') return p.is_hidden;
        if (statusFilter === 'deleted') return p.is_deleted;
        return true;
      })
      .filter((p) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (p.title || '').toLowerCase().includes(q) || (p.nickname || '').toLowerCase().includes(q);
      })
  ), [allPosts, search, statusFilter]);

  useEffect(() => setPage(1), [search, statusFilter]);

  const totalPages = Math.ceil(filteredPosts.length / ROWS_PER_PAGE) || 1;
  const paginated = filteredPosts.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  /* ───────── 엑셀 내보내기 ───────── */
  const handleExportExcel = () => {
    const rows = filteredPosts.map((p) => ({
      ID: p.id,
      카테고리: p.category_name || '',
      제목: p.title || '',
      작성자: p.nickname || '',
      익명여부: p.is_anonymous ? 'Y' : 'N',
      상태: p.is_deleted ? '삭제' : (p.is_hidden ? '블라인드' : '공개'),
      생성일: p.created_at ? new Date(p.created_at).toLocaleString('ko-KR') : '',
      수정일: p.updated_at ? new Date(p.updated_at).toLocaleString('ko-KR') : '',
      좋아요수: p.like_count ?? '',
      조회수: p.view_count ?? '',
    }));

    if (!rows.length) return alert('엑셀로 내보낼 게시글이 없습니다.');

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Posts');
    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `posts_${today}.xlsx`);
  };

  /* ───────── 모달 핸들러 ───────── */
  const openModal = (id, type) => {
    setPendingAction({ id, type });
    setModalOpen(true);
  };

  const handleAction = async (memo) => {
    const { id, type } = pendingAction;
    setModalOpen(false);
    try {
      if (type === 'soft_delete') {
        await api.post(`/api/posts/${id}/soft-delete/`, { memo });
      } else if (type === 'hard_delete') {
        await api.delete(`/api/posts/${id}/permanent-delete/`, { data: { memo } });
      } else if (type === 'blind' || type === 'unblind') {
        await api.post(`/api/posts/${id}/blind/`, { is_hidden: type === 'blind', memo });
      }
      alert('처리되었습니다.');
      await fetchAllPosts();
    } catch {
      alert('처리에 실패했습니다.');
    }
  };

  /* ───────── 로딩 / 에러 ───────── */
  if (loading) return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  /* ───────── 공통 IconButton 스타일 ───────── */
  const iconSx = { p: 0.25, width: 28, height: 28, lineHeight: 1 };

  /* ───────── 상단 검색/필터 바 ───────── */
  const HeaderBar = () => (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      spacing={1.5}
      sx={{ mb: 2, alignItems: isMobile ? 'stretch' : 'center' }}
    >
      <TextField
        label="제목/닉네임"
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth={isMobile}
        sx={{ minWidth: isMobile ? '100%' : 200 }}
      />
      <Select
        size="small"
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        fullWidth={isMobile}
        sx={{ minWidth: isMobile ? '100%' : 140 }}
      >
        <MenuItem value="all">전체</MenuItem>
        <MenuItem value="visible">공개</MenuItem>
        <MenuItem value="hidden">블라인드</MenuItem>
        <MenuItem value="deleted">삭제</MenuItem>
      </Select>

      <Typography variant="body2" color="text.secondary">
        총 {allPosts.length}개 / 현재 {filteredPosts.length}개
      </Typography>

      <Button
        variant="outlined"
        size="small"
        onClick={handleExportExcel}
        fullWidth={isMobile}
      >
        📥 엑셀로 내보내기
      </Button>
    </Stack>
  );

  /* ───────── 모바일: 카드 리스트 ───────── */
  const MobileList = () => (
    <Stack spacing={1.25}>
      {paginated.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
          🔍 조건에 맞는 게시글이 없습니다.
        </Paper>
      ) : (
        paginated.map((post) => {
          const stateLabel = post.is_deleted ? '삭제' : (post.is_hidden ? '블라인드' : '공개');
          const title =
            post.is_deleted ? '삭제된 게시글입니다'
              : post.is_hidden ? '블라인드된 게시글입니다'
                : (post.title || '-');

          const authorLabel = post.is_anonymous
            ? `익명(${post.nickname || '-'})`
            : (post.nickname || '-');

          const created = post.created_at
            ? new Date(post.created_at).toLocaleDateString('ko-KR')
            : '-';

          const canManage = !post.is_deleted;
          const linkHref = `/main/posts/${post.id}`;

          return (
            <Paper key={post.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
              {/* 상단: ID/상태 */}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  ID {post.id}
                </Typography>
                <Typography variant="caption" fontWeight={800}>
                  {stateLabel}
                </Typography>
              </Stack>

              {/* 제목 + 링크 */}
              <Typography
                variant="body2"
                sx={{ mt: 0.5, fontWeight: 800, wordBreak: 'break-word' }}
              >
                {(!post.is_deleted && !post.is_hidden) ? (
                  <a
                    href={linkHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', color: '#1976d2' }}
                  >
                    {title}
                  </a>
                ) : (
                  title
                )}
              </Typography>

              {/* 메타 */}
              <Stack spacing={0.25} sx={{ mt: 1 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">작성자</Typography>
                  <Typography variant="caption" fontWeight={700}>{authorLabel}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">작성일</Typography>
                  <Typography variant="caption" fontWeight={700}>{created}</Typography>
                </Stack>
              </Stack>

              <Divider sx={{ my: 1 }} />

              {/* 액션 */}
              <Stack direction="row" spacing={0.5} justifyContent="space-between" alignItems="center">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => window.open(linkHref, '_blank', 'noopener,noreferrer')}
                  sx={{ flex: 1 }}
                >
                  열기
                </Button>

                <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                  {canManage && (
                    <>
                      <Tooltip title={post.is_hidden ? '숨김 해제' : '블라인드'}>
                        <IconButton
                          size="small"
                          sx={iconSx}
                          color={post.is_hidden ? 'primary' : 'secondary'}
                          onClick={() => openModal(post.id, post.is_hidden ? 'unblind' : 'blind')}
                        >
                          {post.is_hidden
                            ? <VisibilityIcon sx={{ fontSize: 18 }} />
                            : <VisibilityOffIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="삭제">
                        <IconButton
                          size="small"
                          sx={iconSx}
                          color="error"
                          onClick={() => openModal(post.id, 'soft_delete')}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}

                  {userInfo?.role === 'master' && (
                    <Tooltip title="영구삭제">
                      <IconButton
                        size="small"
                        sx={iconSx}
                        color="warning"
                        onClick={() => openModal(post.id, 'hard_delete')}
                      >
                        <DeleteForeverIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>
            </Paper>
          );
        })
      )}
    </Stack>
  );

  /* ───────── PC: 테이블 ───────── */
  const DesktopTable = () => (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        borderRadius: 2,
        '& td, & th': {
          borderBottom: '1px solid #eee',
          p: 1.25,
          whiteSpace: 'nowrap',
        },
        '& th': { bgcolor: 'grey.50', fontWeight: 600 },
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell align="center">ID</TableCell>
            <TableCell align="left">제목</TableCell>
            <TableCell align="center">작성자</TableCell>
            <TableCell align="center">작성일</TableCell>
            <TableCell align="center">상태</TableCell>
            <TableCell align="center" sx={{ width: 140 }}>관리</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {paginated.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                🔍 조건에 맞는 게시글이 없습니다.
              </TableCell>
            </TableRow>
          ) : (
            paginated.map((post) => {
              const titleCell = post.is_deleted
                ? '삭제된 게시글입니다'
                : post.is_hidden
                  ? '블라인드된 게시글입니다'
                  : (
                    <a
                      href={`/main/posts/${post.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'none', color: '#1976d2' }}
                    >
                      {post.title || '-'}
                    </a>
                  );

              const stateLabel = post.is_deleted ? '삭제' : (post.is_hidden ? '블라인드' : '공개');

              const actions = !isTabletOrBelow ? (
                <Stack direction="row" spacing={1} justifyContent="center">
                  {!post.is_deleted && (
                    <>
                      <Button
                        size="small"
                        color={post.is_hidden ? 'primary' : 'secondary'}
                        onClick={() => openModal(post.id, post.is_hidden ? 'unblind' : 'blind')}
                      >
                        {post.is_hidden ? '해제' : '블라인드'}
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => openModal(post.id, 'soft_delete')}
                      >
                        삭제
                      </Button>
                    </>
                  )}
                  {userInfo?.role === 'master' && (
                    <Button
                      size="small"
                      color="warning"
                      onClick={() => openModal(post.id, 'hard_delete')}
                    >
                      영구삭제
                    </Button>
                  )}
                </Stack>
              ) : (
                <Stack direction="row" spacing={0.25} justifyContent="center" alignItems="center">
                  {!post.is_deleted && (
                    <>
                      <Tooltip title={post.is_hidden ? '숨김 해제' : '블라인드'}>
                        <IconButton
                          size="small"
                          sx={{ p: 0.25, width: 24, height: 24 }}
                          color={post.is_hidden ? 'primary' : 'secondary'}
                          onClick={() => openModal(post.id, post.is_hidden ? 'unblind' : 'blind')}
                        >
                          {post.is_hidden
                            ? <VisibilityIcon sx={{ fontSize: 18 }} />
                            : <VisibilityOffIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="삭제">
                        <IconButton
                          size="small"
                          sx={{ p: 0.25, width: 24, height: 24 }}
                          color="error"
                          onClick={() => openModal(post.id, 'soft_delete')}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {userInfo?.role === 'master' && (
                    <Tooltip title="영구삭제">
                      <IconButton
                        size="small"
                        sx={{ p: 0.25, width: 24, height: 24 }}
                        color="warning"
                        onClick={() => openModal(post.id, 'hard_delete')}
                      >
                        <DeleteForeverIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              );

              return (
                <TableRow key={post.id} hover sx={{ '&:hover td': { bgcolor: 'grey.100' } }}>
                  <TableCell align="center">{post.id}</TableCell>
                  <TableCell align="left">{titleCell}</TableCell>
                  <TableCell align="center">
                    {post.is_anonymous ? `익명(${post.nickname})` : (post.nickname || '-')}
                  </TableCell>
                  <TableCell align="center">
                    {post.created_at ? new Date(post.created_at).toLocaleDateString('ko-KR') : '-'}
                  </TableCell>
                  <TableCell align="center">{stateLabel}</TableCell>
                  <TableCell align="center">{actions}</TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ p: isMobile ? 1.5 : 0, width: '100%', minWidth: 0 }}>
      <HeaderBar />

      {isMobile ? <MobileList /> : <DesktopTable />}

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, v) => setPage(v)}
            showFirstButton
            showLastButton
            shape="rounded"
            color="primary"
            size={isMobile ? 'small' : 'medium'}
          />
        </Box>
      )}

      {/* 사유 입력 모달 */}
      <ReasonModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleAction}
        actionLabel={
          pendingAction?.type === 'soft_delete' ? '삭제'
            : pendingAction?.type === 'hard_delete' ? '영구삭제'
              : pendingAction?.type === 'blind' ? '숨김' : '해제'
        }
      />
    </Box>
  );
}
